import { useCallback } from 'react';

import { link } from '@aline/routes';
import type { ApolloLink } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { useMaybeDocumentParams } from 'src/helpers';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

export const useSanityModalStyles = makeStyles()(theme => ({
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 0, 0, 0),
  },
  dialog: {
    borderRadius: theme.dialogStyles.borderRadius,
    margin: '16px',
    width: theme.dialogStyles.standardDialogWidth,
  },
  successBody: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
  },
}));

export function useSendForSignatureHelpers(workflowId: UUID) {
  const navigate = useNavigate();
  const dispatch = useNotificationDispatch();
  const { documentId } = useMaybeDocumentParams();
  const [sendWorkflowForSignature, { loading: sendingForSignature }] =
    Apollo.useSendWorkflowForSignatureMutation();
  const [getWorkflowWithReadyCheck, { loading: gettingWorkflowReady }] =
    Apollo.useGetWorkflowWithReadyCheckLazyQuery({ fetchPolicy: 'network-only' });
  const redirectToDashboard = useCallback(() => navigate(link.DashboardIndex()), [navigate]);
  const redirectToWorkflowBuilder = useCallback(
    () => navigate(link.WorkflowDetailTabSummary({ workflowId })),
    [navigate, workflowId]
  );
  // "Sends" workflow (in some cases, just "play", in others send for signature).
  const proceedWithSending = useCallback(
    async (
      options: {
        showNotReadyReasons?: boolean;
      } = {}
    ): Promise<ApolloLink.Result<Apollo.SendWorkflowForSignatureMutation>> => {
      const { showNotReadyReasons = false } = options;
      const { data } = await getWorkflowWithReadyCheck({
        variables: { workflowId },
      });
      if (!data) return {};

      if (!data.workflow?.isReadyForSignature?.ready) {
        const notReadyReasonsList =
          data.workflow?.isReadyForSignature?.notReadyReasons
            ?.map((reason, index) => {
              const reasonNum = index + 1;
              if (!reason) return '';
              const notReadyForSigReasonString = getNotReadyForSigReasonString(reason);
              return `${reasonNum}. ${notReadyForSigReasonString}`;
            })
            .join(', ') ?? 'no issues';
        if (showNotReadyReasons)
          dispatch({
            payload: {
              key: uuid4(),
              message: `Please fix the following issues before sending for signature: ${notReadyReasonsList}.`,
              variant: 'error',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });

        return {};
      }

      return sendWorkflowForSignature({
        /**
         * The "sent for signature" status row surfaces via the activity feed's own poll; don't
         * refetch GetWorkflowActivity here — it re-runs at cursor:null and clobbers loaded pages.
         */
        refetchQueries: documentId
          ? [
              {
                query: Apollo.GetDocumentBasicMetaDocument,
                variables: { documentId } satisfies Apollo.GetDocumentBasicMetaQueryVariables,
              },
            ]
          : undefined,
        variables: { input: { workflowId } },
      });
    },
    [dispatch, documentId, getWorkflowWithReadyCheck, sendWorkflowForSignature, workflowId]
  );

  return {
    isSubmitting: sendingForSignature || gettingWorkflowReady,
    proceedWithSending,
    redirectToDashboard,
    redirectToWorkflowBuilder,
  };
}

export function getNotReadyForSigReasonString(reason: Apollo.WorkflowNotReadyForSigReason): string {
  switch (reason) {
    case Apollo.WorkflowNotReadyForSigReason.GeneralDocumentsHaveFields:
      return 'Some general documents have fields and must be moved to signing';
    case Apollo.WorkflowNotReadyForSigReason.IntakeFormIncomplete:
      return 'The intake form must be completed before sending for signature';
    case Apollo.WorkflowNotReadyForSigReason.TemplateWorkflow:
      return 'Cannot send template workflow for signature';
    case Apollo.WorkflowNotReadyForSigReason.MissingActions:
      return 'Workflow has missing actions';
    case Apollo.WorkflowNotReadyForSigReason.NoDocuments:
      return 'Workflow has no documents';
    case Apollo.WorkflowNotReadyForSigReason.FieldsRequireSigner:
      return 'Fields must be assigned to a signer recipient';
    case Apollo.WorkflowNotReadyForSigReason.PreSigIncomplete:
      return 'All phases before the signing phase must be completed';
    case Apollo.WorkflowNotReadyForSigReason.SomeActionsUnassigned:
      return 'Must assign all actions prior to sending for signature';
    case Apollo.WorkflowNotReadyForSigReason.WorkflowComplete:
      return 'Workflow already complete';
    case Apollo.WorkflowNotReadyForSigReason.UnresolvedTrackedChanges:
      return 'Some document has unresolved tracked changes';
    case Apollo.WorkflowNotReadyForSigReason.UnresolvedComments:
      return 'Some document has unresolved comments';
  }
}
